import { Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { interval, Subject } from 'rxjs';
import { filter, finalize, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FuseTranslationLoaderService } from '../../../../../../../../@fuse/services/translation-loader.service';
import { CompanyImportResponse, JobStatus } from '../../../../../../../core/model/company-import-text.model';
import { CompanyImportType, CompanyImportTypeLabel, CompanyType } from '../../../../../../../core/model/company.model';
import { PartnerPermission } from '../../../../../../../core/model/user.model';
import {
  RemoteFileRepositoryService,
  RemoteFileType,
} from '../../../../../../../core/service/remote-file-repository.service';
import { MainService } from '../../../../../main.service';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';
import { MassiveCompanyImportDialogService } from './massive-company-import-dialog.service';

@Component({
  selector: 'app-massive-company-import-dialog',
  templateUrl: './massive-company-import-dialog.component.html',
  styleUrls: ['./massive-company-import-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MassiveCompanyImportDialogComponent implements OnDestroy {

  CompanyImportTypeLabel = CompanyImportTypeLabel;

  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  @ViewChild('stepper', { static: true }) stepper: MatStepper;

  textImportForm: UntypedFormGroup;
  xlsImportForm: UntypedFormGroup;

  form: UntypedFormGroup | null = null;

  jobStatus!: CompanyImportResponse;

  JobStatus = JobStatus;
  CompanyType = CompanyType;
  CompanyImportType = CompanyImportType;

  isLoading = false;

  hasMainCompany = true;

  allowDebtorCreate = false;
  allowDebtorParameters = false;

  private _unsubscribeAll: Subject<void> = new Subject<void>();
  private _unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    mainService: MainService,
    private _dialogRef: MatDialogRef<MassiveCompanyImportDialogComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _service: MassiveCompanyImportDialogService,
    private _translationLoader: FuseTranslationLoaderService,
    private _remoteFileRepositoryService: RemoteFileRepositoryService,
  ) {
    this._translationLoader.loadTranslations(italian, english);

    this.allowDebtorCreate = mainService.user.partnerPermissions?.some(p => [PartnerPermission.All, PartnerPermission.DebtorCreate].includes(p));
    this.allowDebtorParameters = mainService.user.partnerPermissions?.some(p => [PartnerPermission.All, PartnerPermission.DebtorParameters].includes(p));

    this.textImportForm = this._formBuilder.group({
        mainCompanyType: new UntypedFormControl(CompanyImportType.seller_debtor, Validators.required),
        text: new UntypedFormControl(null, Validators.required),
      },
    );

    this.xlsImportForm = this._formBuilder.group({
        fileName: new UntypedFormControl(null),
        file: new UntypedFormControl(null, Validators.required),
      },
    );
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    this._unsubscribe.complete();
  }

  previousStepper(): void {
    if (this.jobStatus.status === JobStatus.ERROR) {
      this.jobStatus.status = JobStatus.COMPLETED;
    }

    if (this.stepper.selectedIndex === 3 && !this.hasMainCompany) {
      this.stepper.selectedIndex = 1; // skip insert step
    } else {
      this.stepper.previous();
    }

    this._unsubscribe.next();
  }

  onFormReady(event: UntypedFormGroup): void {
    this.form = event;
  }

  submitText(): void {
    if (this.textImportForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.textImportForm.disable();

    this._service.importText(this.textImportForm.getRawValue())
      .pipe(
        tap(_ => this.stepper.next()),
        switchMap(res => interval(5000)
          .pipe(
            takeUntil(this._unsubscribe),
            takeUntil(this._unsubscribeAll),
            startWith(0),
            switchMap(() => this._service.getJobStatus(res.id)),
            tap(res => this.jobStatus = res),
            filter(_ => this.jobStatus.status === JobStatus.ERROR || this.jobStatus.status === JobStatus.COMPLETED),
            tap(_ => this._unsubscribe.next()),
          ),
        ),
        finalize(() => {
          this.isLoading = false;
          this.textImportForm.enable();
          this.textImportForm.markAsUntouched();
        }),
      )
      .subscribe();
  }

  onFileChange(event: any): void {
    if (event.target.files && event.target.files.length) {
      this.xlsImportForm.patchValue({
        fileName: event.target.files[0].name,
        file: event.target.files[0],
      });
    }
  }

  submitXls(): void {
    if (this.xlsImportForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.xlsImportForm.disable();

    const formData = new FormData();
    formData.append('file', this.xlsImportForm.get('file').value);
    formData.append('mainCompanyType', this.textImportForm.get('mainCompanyType').value);

    this._service.importXls(formData)
      .pipe(
        tap(_ => this.stepper.next()),
        switchMap(res => interval(5000)
          .pipe(
            takeUntil(this._unsubscribe),
            takeUntil(this._unsubscribeAll),
            startWith(0),
            switchMap(() => this._service.getJobStatus(res.id)),
            tap(res => this.jobStatus = res),
            filter(_ => this.jobStatus.status === JobStatus.ERROR || this.jobStatus.status === JobStatus.COMPLETED),
            tap(_ => this._unsubscribe.next()),
          ),
        ),
        finalize(() => {
          this.fileInput.nativeElement.value = null;
          this.isLoading = false;
          this.xlsImportForm.enable();
        }),
      )
      .subscribe();
  }

  updateCompanies(): void {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;
    this.form.disable();

    this._service.importUpdate(this.form.getRawValue())
      .pipe(
        tap(_ => this.stepper.next()),
        finalize(() => {
          this.isLoading = false;
          this.form.enable();
        }),
      )
      .subscribe();
  }

  validateCompanies(): void {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;
    this.form.disable();

    this._service.updateContact(this.form.getRawValue())
      .pipe(
        tap(_ => this.stepper.next()),
        switchMap(res => this._service.importConfirm(res.id)),
        switchMap(res => interval(5000)
          .pipe(
            takeUntil(this._unsubscribe),
            takeUntil(this._unsubscribeAll),
            startWith(0),
            switchMap(() => this._service.getJobStatus(res.id)),
            tap(res => this.jobStatus = res),
            filter(_ => this.jobStatus.status === JobStatus.ERROR || this.jobStatus.status === JobStatus.COMPLETED),
            tap(_ => this._unsubscribe.next()),
          ),
        ),
        tap(res => {
            if (this.jobStatus.status === JobStatus.COMPLETED) {
              this._dialogRef.close(res);
            }
          },
        ),
        finalize(() => {
          this.isLoading = false;
          this.form.enable();
        }),
      )
      .subscribe();
  }

  checkZeroValue(): boolean {
    if (!this.form) {
      return false;
    }

    return this.form.getRawValue()
      .relatedCompanies
      .some(rc => rc.existingInvoiceTotalAmount === 0 || rc.trustRequired === 0);
  }

  navigateNextAfterVerifyStep(): void {
    const mainCompanyImportType = this.jobStatus.parameters.mainCompanyImportType;
    this.hasMainCompany = mainCompanyImportType === CompanyImportType.seller_debtor || mainCompanyImportType === CompanyImportType.debtor_seller;
    if (!this.hasMainCompany) {
      this.stepper.selectedIndex = 3; // skip insert step
      return;
    }
    this.stepper.next();
  }

  downloadXlsTemplate(): void {
    this._remoteFileRepositoryService.downloadFile(RemoteFileType.COMPANY_IMPORT_XLS_TEMPLATE);
  }
}
